import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useCurriculum } from "@/context/CurriculumContext";
import { toast } from "sonner";
import { ScoreRing } from "@/components/Bands";
import { SUBJECT_META_ALL, levelStyle, overallBand } from "@/lib/scale";
import { ChevronLeft, Loader2, Check, Save, Sparkles, Plus } from "lucide-react";

export default function Assess() {
  const { studentId, subject } = useParams();
  const navigate = useNavigate();
  const curriculum = useCurriculum();
  const [student, setStudent] = useState(null);
  const [scores, setScores] = useState({});
  const [observations, setObservations] = useState({});
  const [meta, setMeta] = useState({ strengths: "", areas_for_development: "", next_steps: "" });
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestedTargets, setSuggestedTargets] = useState([]);
  const [addedTargets, setAddedTargets] = useState({});

  useEffect(() => {
    api.get(`/students/${studentId}`).then((r) => {
      setStudent(r.data);
      const prev = r.data.assessments.find((a) => a.subject === subject);
      if (prev) { setScores(prev.scores || {}); setObservations(prev.observations || {}); }
    });
  }, [studentId, subject]);

  if (!curriculum || !student)
    return <div className="flex justify-center py-24 text-slate-300"><Loader2 className="animate-spin" /></div>;

  const subj = curriculum.subjects[subject];
  const tint = SUBJECT_META_ALL[subject].tint;
  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  const allScored = subj.domains.every((d) => scores[d.key]);

  const save = async () => {
    if (!allScored) { toast.error("Please set a level for every skill area."); return; }
    setSaving(true);
    try {
      await api.post("/assessments", {
        student_id: studentId, subject, scores, observations,
        strengths: meta.strengths, areas_for_development: meta.areas_for_development, next_steps: meta.next_steps,
      });
      toast.success("Assessment saved");
      navigate(`/student/${studentId}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const runSuggest = async () => {
    if (Object.keys(scores).length === 0) {
      toast.error("Set at least one skill level first.");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post("/suggest/assessment", { student_id: studentId, subject, scores });
      setMeta((m) => ({
        ...m,
        strengths: data.strengths || m.strengths,
        areas_for_development: data.areas_for_development || m.areas_for_development,
        next_steps: data.next_steps || m.next_steps,
      }));
      setSuggestedTargets(data.targets || []);
      toast.success("AI suggestions ready — review and edit as needed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSuggesting(false);
    }
  };

  const addSuggestedTarget = async (t, idx) => {
    try {
      await api.post("/targets", {
        student_id: studentId, subject, domain: t.domain, text: t.text,
        target_level: t.target_level || null, priority: true,
      });
      setAddedTargets((a) => ({ ...a, [idx]: true }));
      toast.success("Target added");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="pb-28">
      <button
        data-testid="back-to-student"
        onClick={() => navigate(`/student/${studentId}`)}
        className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 transition mb-5"
      >
        <ChevronLeft size={16} /> {student.name}
      </button>

      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: tint }}>{subj.name} Assessment</p>
        <h1 className="font-['Outfit'] text-4xl font-bold text-slate-800 tracking-tight mt-1">{student.name}</h1>
        <p className="text-slate-500 mt-1">{subj.tagline}</p>
      </div>

      <div className="space-y-5">
        {subj.domains.map((d) => (
          <DomainPicker
            key={d.key}
            domain={d}
            tint={tint}
            bandLabels={subj.band_labels}
            value={scores[d.key]}
            onPick={(lvl) => setScores((s) => ({ ...s, [d.key]: lvl }))}
            obs={observations[d.key] || ""}
            onObs={(v) => setObservations((o) => ({ ...o, [d.key]: v }))}
          />
        ))}
      </div>

      {/* Qualitative */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6 mt-5 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="font-['Outfit'] text-lg font-semibold text-slate-800">Summary notes</h3>
          <button
            data-testid="ai-suggest-btn"
            onClick={runSuggest}
            disabled={suggesting}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold font-['Outfit'] text-white transition disabled:opacity-60"
            style={{ background: "linear-gradient(135deg,#81B29A,#5d8a73)" }}
          >
            {suggesting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
            {suggesting ? "Thinking…" : "Suggest with AI"}
          </button>
        </div>
        {[
          ["strengths", "Strengths"],
          ["areas_for_development", "Areas for development"],
          ["next_steps", "Targets / next steps"],
        ].map(([k, label]) => (
          <label key={k} className="block">
            <span className="text-sm font-semibold text-slate-600 font-['Outfit']">{label}</span>
            <textarea
              data-testid={`summary-${k}`}
              rows={k === "strengths" ? 2 : 3}
              value={meta[k]}
              onChange={(e) => setMeta((m) => ({ ...m, [k]: e.target.value }))}
              className="mt-1.5 w-full bg-[#F9F9F7] border border-slate-100 rounded-xl px-4 py-2.5 outline-none focus:border-[#81B29A] focus:bg-white resize-none transition"
            />
          </label>
        ))}

        {suggestedTargets.length > 0 && (
          <div className="rounded-xl border border-[#81B29A]/30 bg-[#81B29A]/5 p-4" data-testid="ai-suggested-targets">
            <p className="text-sm font-semibold text-slate-700 font-['Outfit'] flex items-center gap-1.5 mb-3">
              <Sparkles size={15} className="text-[#5d8a73]" /> Suggested SMART targets
            </p>
            <div className="space-y-2">
              {suggestedTargets.map((t, idx) => {
                const dom = subj.domains.find((d) => d.key === t.domain);
                return (
                  <div key={`${t.domain}-${t.text}`} className="flex items-start gap-3 bg-white rounded-lg p-3 border border-slate-100">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700">{t.text}</p>
                      <p className="text-xs text-slate-400 mt-1">{dom?.name}{t.target_level ? ` · target level ${t.target_level}` : ""}</p>
                    </div>
                    <button
                      data-testid={`add-suggested-target-${idx}`}
                      onClick={() => addSuggestedTarget(t, idx)}
                      disabled={addedTargets[idx]}
                      className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold font-['Outfit'] transition disabled:opacity-60"
                      style={addedTargets[idx] ? { background: "#DCEBE4", color: "#3f7a5c" } : { background: "#4A5D6B", color: "#fff" }}
                    >
                      {addedTargets[idx] ? <Check size={13} /> : <Plus size={13} />}
                      {addedTargets[idx] ? "Added" : "Add"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/90 backdrop-blur border-t border-slate-200 no-print">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ScoreRing total={total} size={52} />
            <div>
              <p className="font-['Outfit'] font-bold text-slate-800 leading-tight">{overallBand(total).name}</p>
              <p className="text-xs text-slate-400">{Object.keys(scores).length}/{subj.domains.length} skills scored</p>
            </div>
          </div>
          <button
            data-testid="save-assessment-btn"
            onClick={save}
            disabled={saving || !allScored}
            className="rounded-full px-6 py-3 font-semibold font-['Outfit'] text-white transition flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: "#4A5D6B" }}
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />} Save assessment
          </button>
        </div>
      </div>
    </div>
  );
}

function DomainPicker({ domain, tint, bandLabels, value, onPick, obs, onObs }) {
  const selected = domain.levels.find((l) => l.level === value);
  const labels = bandLabels || ["Emerging", "Developing", "Secure", "Adv"];
  return (
    <div data-testid={`domain-${domain.key}`} className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(42,59,71,0.04)] p-6">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="font-['Outfit'] text-xl font-semibold text-slate-800">{domain.name}</h3>
        <span className="text-xs text-slate-400">{domain.blurb}</span>
      </div>

      <div className="flex gap-1.5 mt-4 flex-wrap">
        {domain.levels.map((l) => {
          const st = levelStyle(l.level);
          const active = value === l.level;
          return (
            <button
              key={l.level}
              data-testid={`level-${domain.key}-${l.level}`}
              onClick={() => onPick(l.level)}
              className="h-11 flex-1 min-w-[36px] rounded-xl font-['Outfit'] font-bold transition-all border-2"
              style={{
                backgroundColor: active ? st.solid : st.soft,
                borderColor: active ? st.solid : "transparent",
                color: active ? "#fff" : "#475569",
                transform: active ? "translateY(-2px)" : "none",
                boxShadow: active ? "0 6px 14px rgba(42,59,71,0.15)" : "none",
              }}
            >
              {l.level}
            </button>
          );
        })}
      </div>

      <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-300 font-bold mt-1.5 px-1">
        <span>{labels[0]}</span><span>{labels[1]}</span><span>{labels[2]}</span><span>{labels[3]}</span>
      </div>

      {selected && (
        <div className="mt-4 rounded-xl p-4 animate-rise" style={{ backgroundColor: levelStyle(value).soft }}>
          <div className="flex items-start gap-2">
            <Check size={16} className="mt-0.5 shrink-0" style={{ color: levelStyle(value).solid }} />
            <div>
              <p className="font-semibold text-slate-800 text-sm">Level {selected.level}: {selected.descriptor}</p>
              <p className="text-sm text-slate-500 mt-0.5">{selected.evidence}</p>
            </div>
          </div>
        </div>
      )}

      <input
        data-testid={`obs-${domain.key}`}
        value={obs}
        onChange={(e) => onObs(e.target.value)}
        placeholder="Key observations (optional)…"
        className="mt-3 w-full bg-[#F9F9F7] border border-slate-100 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#81B29A] focus:bg-white transition"
      />
    </div>
  );
}
